import { Controller } from "@hotwired/stimulus";
import { visitPreferredLanguageSettings } from "../../Shared/Localization/PreferredLanguageNavigation";

export default class PreferredLanguagesController extends Controller<HTMLElement> {
    static targets = ["editButton", "list", "orderFields", "reorderForm", "removeForm", "status", "confirmDialog", "confirmMessage"];
    static values = { error: String };
    declare readonly editButtonTarget: HTMLButtonElement;
    declare readonly listTarget: HTMLOListElement;
    declare readonly orderFieldsTarget: HTMLElement;
    declare readonly reorderFormTarget: HTMLFormElement;
    declare readonly removeFormTargets: HTMLFormElement[];
    declare readonly statusTarget: HTMLElement;
    declare readonly confirmDialogTarget: HTMLDialogElement;
    declare readonly confirmMessageTarget: HTMLElement;
    declare readonly errorValue: string;
    private editing = false;
    private pending = false;
    private pendingRemoval?: HTMLFormElement;
    private confirmedRemoval?: HTMLFormElement;
    private abort?: AbortController;
    private dragTimer?: ReturnType<typeof setTimeout>;
    private dragRow?: HTMLElement;
    private dragOrder: string[] = [];
    private dragging = false;

    disconnect(): void { this.abort?.abort(); this.finishDragListeners(); }

    toggleEdit(): void {
        if (this.pending) return;
        this.editing = !this.editing;
        this.editButtonTarget.textContent = this.editing ? this.editButtonTarget.dataset.doneLabel! : this.editButtonTarget.dataset.editLabel!;
        this.editButtonTarget.setAttribute("aria-pressed", String(this.editing));
        this.removeFormTargets.forEach(form => form.hidden = !this.editing);
        this.listTarget.querySelectorAll<HTMLButtonElement>(".profile-drag-handle").forEach(button => button.disabled = this.editing);
    }

    confirmRemove(event: Event): void {
        const form = event.currentTarget as HTMLFormElement;
        if (this.confirmedRemoval === form) { this.confirmedRemoval = undefined; this.pending = true; return; }
        event.preventDefault();
        if (this.pending) return;
        this.pendingRemoval = form;
        this.confirmMessageTarget.textContent = form.dataset.confirmMessage ?? "";
        this.confirmDialogTarget.showModal();
    }

    removeConfirmed(): void {
        const form = this.pendingRemoval;
        if (!form || this.pending) return;
        this.confirmedRemoval = form; this.confirmDialogTarget.close(); form.requestSubmit();
    }

    removeEnded(event: CustomEvent<{ success: boolean }>): void {
        if (event.detail.success) return;
        this.pending = false;
        this.confirmedRemoval = undefined;
        this.statusTarget.textContent = this.errorValue;
    }

    closeOutside(event: Event): void { if (event.target === this.confirmDialogTarget) this.confirmDialogTarget.close(); }

    moveWithKeyboard(event: KeyboardEvent): void {
        if (this.pending || this.editing || !["ArrowUp", "ArrowDown"].includes(event.key)) return;
        const row = (event.currentTarget as HTMLElement).closest<HTMLElement>("[data-language]");
        if (!row) return;
        const other = event.key === "ArrowUp" ? row.previousElementSibling : row.nextElementSibling;
        if (!other) return;
        event.preventDefault(); const previous = this.order();
        this.listTarget.insertBefore(row, event.key === "ArrowUp" ? other : other.nextElementSibling);
        this.statusTarget.textContent = `${row.dataset.language!.toUpperCase()} ${this.order().indexOf(row.dataset.language!) + 1}/${this.order().length}`;
        void this.persist(previous, row.dataset.language);
    }

    startDrag(event: PointerEvent): void {
        if (this.pending || this.editing || event.button > 0) return;
        const handle = event.currentTarget as HTMLElement;
        this.dragRow = handle.closest<HTMLElement>("[data-language]") ?? undefined;
        if (!this.dragRow) return;
        event.preventDefault(); handle.setPointerCapture?.(event.pointerId);
        this.dragOrder = this.order();
        if (event.pointerType === "touch") this.dragTimer = setTimeout(() => this.dragging = true, 200);
        else this.dragging = true;
        document.addEventListener("pointermove", this.dragMove);
        document.addEventListener("pointerup", this.dragEnd);
        document.addEventListener("pointercancel", this.dragCancel);
    }

    private readonly dragMove = (event: PointerEvent): void => {
        if (!this.dragging || !this.dragRow) return;
        const target = document.elementFromPoint(event.clientX, event.clientY)?.closest<HTMLElement>("[data-language]");
        if (!target || target === this.dragRow || target.parentElement !== this.listTarget) return;
        const rows = [...this.listTarget.children];
        this.listTarget.insertBefore(this.dragRow, rows.indexOf(this.dragRow) < rows.indexOf(target) ? target.nextElementSibling : target);
    };

    private readonly dragEnd = (): void => {
        const language = this.dragRow?.dataset.language;
        const changed = this.dragging && this.order().join(".") !== this.dragOrder.join(".");
        this.finishDragListeners();
        if (changed) void this.persist(this.dragOrder, language);
    };

    private readonly dragCancel = (): void => { this.restore(this.dragOrder); this.finishDragListeners(); };

    private finishDragListeners(): void {
        clearTimeout(this.dragTimer); this.dragging = false; this.dragRow = undefined;
        document.removeEventListener("pointermove", this.dragMove); document.removeEventListener("pointerup", this.dragEnd); document.removeEventListener("pointercancel", this.dragCancel);
    }

    private order(): string[] { return [...this.listTarget.querySelectorAll<HTMLElement>(":scope > [data-language]")].map(row => row.dataset.language!); }

    private restore(order: string[]): void {
        const rows = [...this.listTarget.querySelectorAll<HTMLElement>(":scope > [data-language]")];
        order.forEach(language => { const row = rows.find(item => item.dataset.language === language); if (row) this.listTarget.append(row); });
    }

    private async persist(previous: string[], focusLanguage?: string): Promise<void> {
        if (this.pending) return;
        this.pending = true; this.statusTarget.textContent = "";
        this.orderFieldsTarget.replaceChildren(...this.order().map(language => {
            const input = document.createElement("input"); input.type = "hidden"; input.name = "languages"; input.value = language; return input;
        }));
        this.abort = new AbortController();
        try {
            const response = await fetch(this.reorderFormTarget.action, { method: "POST", body: new FormData(this.reorderFormTarget), credentials: "same-origin", signal: this.abort.signal });
            if (!response.ok) throw new Error(String(response.status));
            visitPreferredLanguageSettings(focusLanguage);
        } catch (error) {
            if (!this.abort.signal.aborted) { this.restore(previous); this.statusTarget.textContent = this.errorValue; }
        } finally { this.pending = false; }
    }
}
